<template>
  <div
    v-if="googleAvailable && gClientId"
    class="google-auth-button"
  >
    <DarwinButton
      class="fake-google-button"
      flair="rounded"
      kind="outline"
      size="lg"
    >
      <template #icon-prepend>
        <IconColoredGoogle />
      </template>
      {{ isRegister ? 'Signup with Google' : 'Continue with Google' }}
    </DarwinButton>
    <div class="google-iframe">
      <div
        id="g_id_onload"
        :data-client_id="gClientId"
        :data-nonce="token"
        :data-login_uri="googleSSOLoginUrl"
        data-ux_mode="redirect"
        data-auto_prompt="false"
      />
      <div
        class="g_id_signin"
        :class="{ hide: isDisabled }"
        data-type="standard"
        data-theme="outline"
        data-size="large"
        :data-text="isRegister ? 'signup_with' : 'continue_with'"
        data-shape="pill"
        data-width="298"
        data-logo_alignment="center"
      />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'GoogleAuthButton',
}
</script>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import IconColoredGoogle from '@/assets/icons/IconColoredGoogle.svg?inline'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import { useToast } from '@/uiKit/Toast/useToast'
import { BASE_API, GOOGLE_CLIENT_ID } from '@/services/config'

// eslint-disable-next-line vue/define-props-declaration
defineProps({
  isRegister: {
    required: false,
    default: false,
    type: Boolean,
  },
  isDisabled: {
    required: false,
    default: false,
    type: Boolean,
  },
  token: { required: false, type: String, default: '' },
})

const toast = useToast()

const gClientId = ref<string | null>(GOOGLE_CLIENT_ID)
const googleSSOLoginUrl = ref<string>(`${BASE_API}/users/authenticate/sso/oauth/google/validate`)
const googleAvailable = ref<boolean>(true)

const findScript = ref((src: string): HTMLScriptElement | undefined => {
  const scripts = Array.from(document.getElementsByTagName('script'))
  const found = scripts.find((script) => script.getAttribute('src') === src)
  return found
})

const loadGoogleAuthScripts = (): Promise<boolean> =>
  new Promise<boolean>((resolve, reject) => {
    const srcScript = 'https://accounts.google.com/gsi/client'
    const existingScript = findScript.value(srcScript)
    if (existingScript) {
      document.body.removeChild(existingScript)
    }
    const googleAuthScript = document.createElement('script')
    googleAuthScript.setAttribute('src', srcScript)
    googleAuthScript.async = true
    googleAuthScript.defer = true
    googleAuthScript.onload = function (): void {
      resolve(true)
    }
    googleAuthScript.onerror = (err): void => {
      reject(err)
    }
    document.body.appendChild(googleAuthScript)
  })

onMounted(async (): Promise<void> => {
  try {
    await loadGoogleAuthScripts()
  } catch {
    googleAvailable.value = false

    toast.error({
      meta: { title: 'Google sign-on is currently unavailable' },
      duration: 3000,
    })
  }
})
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';

.google-auth-button {
  position: relative;
  cursor: pointer;
  border-radius: 100px;
  &:hover {
    .fake-google-button {
      background-color: #f4f5f5;
    }
  }

  .google-iframe {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    opacity: 0;
  }

  .fake-google-button {
    width: 100%;
    pointer-events: none;
    position: relative;
    z-index: 1;
    @include fontRegularBody300;
  }
}
</style>
